import {APPROVED_KYC_STATUSES} from './kycStatus';

// Guarded: the variable is absent under jest, and a bare `.includes` here
// took down every suite that reached this module.
export const isDev = (process.env.REACT_APP_API_BASE_URL || '').includes(
  '.daba-dev.',
);

export const chartConfig = date => {
  return {
    showPoints: false,
    customDataParse: {
      xAxisKey: 'date',
      yAxisKey: 'amount',
    },
    xAxisConfig: {
      display: true,

      type: 'time',
      time: {
        unit: 'month',
        displayFormats: {
          quarter: 'WW YY',
        },
      },
    },
    yAxisConfig: {
      display: true,
      position: 'left',
    },
  };
};

export const barChartDataset = (data, labels) => {
  return {
    labels,
    datasets: [
      {
        label: 'Yearly Users Chart',
        backgroundColor: '#D8CDFC',
        borderColor: '#D8CDFC',
        borderWidth: 0,
        data,
        maxBarThickness: 40,
        categoryPercentage: 0.2,
        barPercentage: 0.5,
      },
    ],
  };
};

export const usersLineChartControls = [
  {label: 'DAY', format: 'L', title: 'DAILY'},
  {label: 'WEEK', format: 'WW', title: 'WEEKLY'},
  {label: 'MONTH', format: 'MMMM YYYY', title: 'MONTHLY'},
  {label: 'YEAR', format: 'YYYY', title: 'YEARLY'},
];

export const notificationLanguages = [
  {label: 'English', key: 'EN'},
  {label: 'French', key: 'FR'},
];

export const downloadUsersHeader = [
  {label: 'First Name', key: 'firstName'},
  {label: 'Last Name', key: 'lastName'},
  {label: 'Email', key: 'email'},
  {label: 'Phone Number', key: 'phoneNumber'},
  {label: 'Year of Birth', key: 'dateOfBirth'},
  {label: 'Accredited', key: 'isAccredited'},
  {label: 'Profile Completion(%)', key: 'completionPercentage'},
  {label: 'Country of Residence', key: 'country'},
  {label: 'Country of Citizenship', key: 'countryOfCitizenship'},
  {label: 'Annual Net Income', key: 'netIncome'},
  {label: 'Employment Status', key: 'employmentStatus'},
  {label: 'Estimated Net Worth', key: 'netWorth'},
  {label: 'Investment Experience', key: 'investmentExperience'},
  {label: 'When would you need to take out your Investment?', key: 'takeOut'},
  {label: 'How do you see yourself investing?', key: 'howInvest'},
  {label: 'User Topics', key: 'topics'},
  {label: 'User Transactions', key: 'transactions'},
  {label: 'User Reservations', key: 'reservations'},

  {label: 'Country of Interests', key: 'countryInterests'},

  {label: 'Device', key: 'device'},
  {label: 'Last Session Date Time', key: 'time'},

  {label: 'Current Version', key: 'appVersion'},
  {label: 'Verification Method', key: 'verificationMethod'},
  {label: 'Date Joined', key: 'joined'},
];

export const notificationOptions = [
  {label: 'MONDAY', value: 'Monday'},
  {label: 'TUESDAY', value: 'Tuesday'},
  {label: 'WEDNESDAY', value: 'Wednesday'},
  {label: 'THURSDAY', value: 'Thursday'},
  {label: 'FRIDAY', value: 'Friday'},
  {label: 'SATURDAY', value: 'Saturday'},
  {label: 'SUNDAY', value: 'Sunday'},
];

export const notificationFilterOptions = [
  {value: 'EITHER', label: 'Either'},
  {value: 'YES', label: 'Yes'},
  {value: 'NO', label: 'No'},
];

export const usersFilterEnumOptions = [
  {value: 'EITHER', label: 'Either'},
  {value: 'TRUE', label: 'Yes'},
  {value: 'FALSE', label: 'No'},
];

/** RegionCategory — `getAllUsers` filter `user.regionCategory` (array) */
export const REGION_CATEGORY_FILTER_OPTIONS = [
  {value: 'NORTH_AMERICA', label: 'North America'},
  {value: 'SOUTH_AMERICA', label: 'South America'},
  {value: 'EUROPE', label: 'Europe'},
  {value: 'NORTH_AFRICA', label: 'North Africa'},
  {value: 'CENTRAL_WEST_AFRICA', label: 'Central & West Africa'},
  {value: 'SOUTHERN_EAST_AFRICA', label: 'Southern & East Africa'},
  {value: 'WAEMU', label: 'WAEMU'},
  {value: 'REST_OF_WORLD', label: 'Rest of world'},
];

/** UserRiskProfile — `getAllUsers` filter `user.riskProfile` (array) */
export const USER_RISK_PROFILE_FILTER_OPTIONS = [
  {value: 'CONSERVATIVE', label: 'Conservative'},
  {value: 'MODERATE', label: 'Moderate'},
  {value: 'AGGRESSIVE', label: 'Aggressive'},
];

/** PortfolioWeight — `getAllUsers` filter `user.portfolioWeight` (array) */
export const USER_PORTFOLIO_WEIGHT_FILTER_OPTIONS = [
  {value: 'OVERWEIGHT', label: 'Overweight'},
  {value: 'UNDERWEIGHT', label: 'Underweight'},
  {value: 'BALANCED', label: 'Balanced'},
  {value: 'UNKNOWN', label: 'Unknown'},
];

/** UserSegment — `getAllUsers` filter `user.userSegments` (array) */
const USER_SEGMENT_ACRONYMS = {AUM: true};
const USER_SEGMENT_VALUES = [
  'PORTFOLIO_CONCENTRATION_RISK',
  'HIGH_CASH_POSITION',
  'DEPOSIT_TO_INVESTMENT_GAP',
  'ASSET_CLASS_RESEARCH_PATTERN',
  'RECENT_LIQUIDATION_NO_REINVESTMENT',
  'RESEARCH_HEAVY_LOW_ACTION',
  'HIGH_AUM_HIGH_ACTIVITY_NO_PRO',
  'PERFORMANCE_TRACKING_OBSESSION',
  'VIEWED_DABA_PRO_PAGE_NO_SUBSCRIPTION',
  'FUNDED_BUT_INACTIVE',
  'SMALL_FREQUENT_DEPOSITS',
  'ENGAGEMENT_EXECUTION_GAP',
  'NEW_USER_NO_INVESTMENT',
  'INVESTMENT_FREQUENCY_MISMATCH',
  'WATCHLIST_ACROSS_ASSET_CLASSES',
  'FIRST_TIME_INVESTOR_STOCKS_ONLY',
  'HAS_FUNDS_NO_RECENT_INVESTMENT',
  'MARKET_VOLATILITY_SPIKE_ENGAGEMENT',
  'PREMIUM_CONTENT_CONSUMPTION',
  'TRIAL_ENDED_NO_CONVERSION',
  'PORTFOLIO_TOOL_USAGE',
  'SCREENER_POWER_USER',
  'BASIC_INVESTMENT_QUESTIONS_TO_SUPPORT',
  'CONSERVATIVE_HOLDINGS_GROWTH_CONTENT',
  'TUTORIAL_COMPLETION_NO_INVESTMENT',
  'RISK_PROFILE_MISMATCH',
  'RAPID_AUM_GROWTH_BASIC_PORTFOLIO',
  'RESEARCH_HEAVY_LOW_ACTION_PROFILE',
  'SINGLE_ASSET_CLASS_HOLDER',
  'SINGLE_ASSET_CLASS_EXPOSURE',
  'CASH_FULLY_IDLE',
  'CASH_LARGELY_IDLE',
  'CASH_PARTIALLY_DEPLOYED',
  'CASH_LIGHTLY_IDLE',
  'CASH_DEPLOYED',
  'NO_FUNDS',
  'FAST_DEPOSITOR_FAST_INVESTOR',
  'SLOW_DEPLOYER',
  'RESEARCH_THEN_BUY',
  'BROWSER_NO_BUY',
  'WHALE_DORMANT',
  'SUBSCRIBED_NOT_USING',
  'HIGH_DISCRETIONARY_LOW_DEPOSIT',
  'DIASPORA_REMITTANCE_PATTERN',
  'EXPAT_HIGH_CAPACITY',
  'LOW_ABSOLUTE_TOP_QUINTILE',
  'UNDER_DECLARED',
];

export const USER_SEGMENT_FILTER_OPTIONS = USER_SEGMENT_VALUES.map(value => ({
  value,
  label: value
    .split('_')
    .map(word =>
      USER_SEGMENT_ACRONYMS[word]
        ? word
        : word.charAt(0) + word.slice(1).toLowerCase(),
    )
    .join(' '),
}));

export const notificationLastVisitOptions = [
  {label: 'Either', value: 'EITHER'},
  {label: 'Older than a week', value: 'OLDER_THAN_A_WEEK'},
  {label: 'Older than a month', value: 'OLDER_THAN_A_MONTH'},
  {label: 'Older than a three months', value: 'OLDER_THAN_THREE_MONTHS'},
  {label: 'Older than a six month', value: 'OLDER_THAN_SIX_MONTH'},
];

export const notificationTypes = [
  {label: 'ANNOUNCEMENTS', value: 'Announcements'},
  {label: 'HOMEPAGE', value: 'Homepage'},
  {label: 'STOCKS', value: 'Stocks'},
  {label: 'DABA_PRO_SCREEN', value: 'Daba Pro Page'},
  {label: 'START_KYC', value: 'KYC Onboarding Page'},
  {label: 'SINGLE_STOCK', value: 'Single Stock / Public Equity'},
  {label: 'MUTUAL_FUNDS', value: 'Mutual Fund'},
  {label: 'SINGLE_MUTUAL_FUND', value: 'Single Mutual Fund'},
  {label: 'SIMULATOR', value: 'Simulator'},
  {label: 'BONDS', value: 'Bonds'},
  {label: 'SINGLE_BOND', value: 'Single Bond'},
  // Instrument v2 destinations. The composer previously had no way to target
  // an IPO, a startup/private equity or a venture fund at all.
  {label: 'IPO', value: 'IPOs'},
  {label: 'SINGLE_IPO', value: 'Single IPO'},
  {label: 'VENTURE', value: 'Startups'},
  {label: 'SINGLE_VENTURE', value: 'Single Startup / Private Equity'},
  {label: 'VENTURE_FUND', value: 'Venture Funds'},
  {label: 'SINGLE_VENTURE_FUND', value: 'Single Venture Fund'},
  {label: 'LEADERBOARD', value: 'Leaderboard'},
  {label: 'OPPORTUNITY_PAGE', value: 'Opportunity Page'},
  {label: 'INTELLIGENCE_PAGE', value: 'Intelligence Page'},
  {label: 'GROUPS_PAGE', value: 'Groups Page'},
  {label: 'SUBSCRIPTION_PLAN', value: 'User Subscription Page'},
  {label: 'SELECT_SUBSCRIPTION_PLAN', value: 'Select Subscription Page'},
  {label: 'OPPORTUNITY_BY_ID', value: 'Single Opportunity'},
  {label: 'SINGLE_COLLECTION', value: 'Single Collection'},
  {label: 'COLLECTIONS', value: 'Collections'},
  {label: 'REPORT_BY_ID', value: 'Single Report'},
  {label: 'GROUP_BY_ID', value: 'Single Group'},
  {label: 'COMPANY_BY_ID', value: 'Single Company'},
  {label: 'DIVIDENDS_ANNOUNCEMENT', value: 'Dividend Announcement'},
  {
    label: 'EXTERNAL_LINK',
    value: 'External Link',
  },
];

export const emailNotificationTypes = [
  {label: 'ANNOUNCEMENTS', value: 'Announcements'},
  {label: 'GENERAL', value: 'General'},
  {label: 'DIVIDENDS_ANNOUNCEMENT', value: 'Dividend Announcement'},
];

export const assetClassEnum = [
  {label: 'IPO', value: 'IPO'},
  {label: 'STOCK', value: 'STOCK'},
  {label: 'VENTURE', value: 'VENTURE'},
];

export const audienceTypes = [
  {value: 'ALL_USERS', label: 'All Users'},
  {value: 'USERS', label: 'User(s)'},
  {value: 'INTERNAL_TEAM_MEMBERS', label: 'Internal Team Members'},
  {value: 'USERS_BY_GROUP', label: 'Users in Group(s)'},
  {value: 'INVESTORS_IN_A_CAMPAIGN', label: 'Investors in a Campaign'},
  {value: 'INVESTORS_WITH_RESERVATIONS', label: 'Investors with Reservations'},
];

export const paymentMethodOptions = [
  {value: 'CREDITCARD', label: 'Credit Card'},
  {value: 'WIRE_AUTO', label: 'Wire Auto'},
  {value: 'ACH_MANUAL', label: 'ACH Manual'},
  {value: 'WIRE_MANUAL', label: 'Wire Manual'},
  {value: 'CRYPTO_MANUAL', label: 'Crypto Manual'},
  {value: 'CRYPTO_AUTO', label: 'Crypto Auto'},
  {value: 'ACH_AUTO', label: 'ACH Auto'},
];

export const noOfEmployeesOptions = [
  {value: 'BETWEEN_ZERO_AND_ONE', label: 'Between 0 & 1'},
  {value: 'BETWEEN_TWO_AND_TEN', label: 'Between 2 & 10'},
  {value: 'BETWEEN_ELEVEN_AND_FIFTY', label: 'Between 11 & 50'},
  {value: 'BETWEEN_FIFTY_ONE_AND_TWO_HUNDRED', label: 'Between 51 & 200'},
  {value: 'BETWEEN_TWO_HUNDRED_AND_FIVE_HUNDRED', label: 'Between 200 & 500'},
  {
    value: 'BETWEEN_FIVE_HUNDRED_AND_ONE_AND_ONE_THOUSAND',
    label: 'Between 501 & 1,000',
  },
  {
    value: 'BETWEEN_ONE_THOUSAND_AND_FIVE_THOUSAND',
    label: 'Between 1,000 & 5,000',
  },
  {
    value: 'BETWEEN_FIVE_THOUSAND_AND_TEN_THOUSAND',
    label: 'Between 5,000 & 10,000',
  },
  {value: 'MORE_THAN_TEN_THOUSAND', label: 'More than 10,000'},
];

export const transactionStatusOptions = [
  {value: 'REFUND_PENDING', label: 'Refund Pending'},
  {value: 'REFUND_SUCCESSFUL', label: 'Refund Successful'},
  {value: 'CREATED', label: 'Created'},
  {value: 'SETTLED', label: 'Settled'},
  {value: 'FAILED', label: 'Failed'},
  {value: 'FUNDED', label: 'Funded'},
  {value: 'VOIDED', label: 'Voided'},
  {value: 'CANCELLED', label: 'Canceled'},
];

export const walletTransactionDirectionOptions = [
  {
    value: 'USER_BANK_ACCOUNT_TO_USER_WALLET',
    label: 'User Bank Account to User Wallet',
  },
  {
    value: 'USER_WALLET_TO_USER_BANK_ACCOUNT',
    label: 'User Wallet to User Bank Account',
  },
  {value: 'USER_WALLET_TO_BUY_ORDER', label: 'User Wallet to Buy Order'},
  {value: 'USER_WALLET_TO_SELL_ORDER', label: 'User Wallet to Sell Order'},
  {
    value: 'USER_WALLET_TO_VENTURE_INVESTMENT',
    label: 'User Wallet to Venture Investment',
  },
  {
    value: 'USER_WALLET_TO_VENTURE_FUND_INVESTMENT',
    label: 'User Wallet to Venture Fund Investment',
  },
  {
    value: 'USER_WALLET_TO_IPO_INVESTMENT',
    label: 'User Wallet to IPO Investment',
  },
  {value: 'DIVIDEND_PAYOUT', label: 'Dividend Payout'},
  {value: 'DIVIDEND_REVERSAL', label: 'Dividend Reversal'},
  {value: 'CURRENCY_SWAP', label: 'Currency Swap'},
  {
    value: 'PLATFORM_CREDIT_USER_BALANCE',
    label: 'Platform Credit User Balance',
  },
  {value: 'PLATFORM_DEBIT_USER_BALANCE', label: 'Platform Debit User Balance'},
  {
    value: 'USER_WALLET_TO_STARTUP_INVESTMENT',
    label: 'User Wallet to Startup Investment',
  },
  {value: 'REWARDS_PAYIN', label: 'Rewards Payin'},
  {value: 'DEPOSIT', label: 'Deposit'},
  {value: 'WITHDRAWAL', label: 'Withdrawal'},
  {value: 'LOCK_AMOUNT', label: 'Lock Amount'},
  {value: 'UNLOCK_AMOUNT', label: 'Unlock Amount'},
  {value: 'SELL_ORDER', label: 'Sell Order'},
  {value: 'BUY_ORDER', label: 'Buy Order'},
  {value: 'PLATFORM_FEE', label: 'Platform Fee'},
  {value: 'SAVINGS_WITHDRAWAL', label: 'Savings Withdrawal'},
  {value: 'SAVINGS_DEPOSIT', label: 'Savings Deposit'},
  {value: 'SAVINGS_DEPOSIT_REVERSAL', label: 'Savings Deposit Reversal'},
  {value: 'SAVINGS_WITHDRAWAL_REVERSAL', label: 'Savings Withdrawal Reversal'},
  {value: 'STARTUP_INVESTMENT_REVERSAL', label: 'Startup Investment Reversal'},
  {value: 'VENTURE_INVESTMENT_REVERSAL', label: 'Venture Investment Reversal'},
  {
    value: 'VENTURE_FUND_INVESTMENT_REVERSAL',
    label: 'Venture Fund Investment Reversal',
  },
  {value: 'IPO_INVESTMENT_REVERSAL', label: 'IPO Investment Reversal'},
  {value: 'DABA_PRO', label: 'DABA Pro'},
  {value: 'DABA_ACADEMY', label: 'Daba Academy'},
];

export const notificationOrderStatusOptions = [
  {key: 'CANCELLED', name: 'Cancelled'},
  {key: 'ERROR', name: 'Error'},
  {key: 'EXPIRED', name: 'Expired'},
  {key: 'FILLED', name: 'Filled'},
  {key: 'OPEN', name: 'Open'},
  {key: 'PARTIALLY_FILLED', name: 'Partially Filled'},
  {key: 'PENDING', name: 'Pending'},
  {key: 'REJECTED', name: 'Rejected'},
  {key: 'SETTLED', name: 'Settled'},
  {key: 'CREATED', name: 'Created'},
  {key: 'FAILED', name: 'Failed'},
  {key: 'FUNDED', name: 'Funded'},
  {key: 'PARTIALLY_SETTLED', name: 'Partially Settled'},
  {key: 'REFUND_PENDING', name: 'Refund Pending'},
  {key: 'REFUND_SUCCESSFUL', name: 'Refund Successful'},
  {key: 'VOIDED', name: 'Voided'},
];

export const notificationAssetClasses = [
  {key: 'IPO', name: 'IPO'},
  {key: 'BOND', name: 'Bond'},
  {key: 'MUTUAL_FUND', name: 'Mutual Fund'},
  {key: 'STOCK', name: 'Stock'},
  {key: 'VENTURE', name: 'Venture'},
  {key: 'VENTURE_FUND', name: 'Venture Fund'},
];

export const transactionTypes = [
  {value: 'STRATEGY', label: 'Strategy'},
  {value: 'VENTURE', label: 'Venture'},
  {value: 'IPO', label: 'IPO'},
  {value: 'STOCK', label: 'Stock'},
  {value: 'REFUND', label: 'Refund'},
];

export const ADMIN_USER_TYPES = [
  {
    key: 'ALL',
    label: 'All Users',
    filterProperty: 'kycStatus',
    filterPropertyValue: undefined,
    downloadName: 'All Users',
    labelTitle: 'All Users',
  },
  {
    key: 'UNVERIFIED',
    filterProperty: 'kycStatus',
    filterPropertyValue: 'NOT_STARTED',
    label: 'Unverified',
    downloadName: 'List of Unverified KYC Users',
    labelTitle: 'Unverified KYC Users',
  },
  {
    key: 'PENDING',
    filterProperty: 'kycStatus',
    filterPropertyValue: 'PENDING',
    label: 'Pending',
    downloadName: 'List of Users with Pending KYC',
    labelTitle: 'Pending KYC Users',
  },
  {
    key: 'PENDING VERIFICATION',
    filterProperty: 'kycStatus',
    filterPropertyValue: 'PENDING_VERIFICATION',
    label: 'Pending Verification',
    downloadName: 'List of Users with Pending KYC Verification',
    labelTitle: 'Pending Verified Users',
  },
  {
    // Both approval states, through the plural filter: a conditionally
    // approved user is approved, and with the singular `kycStatus` they would
    // show up under no tab on this page at all.
    key: 'VERIFIED',
    filterProperty: 'kycStatuses',
    filterPropertyValue: APPROVED_KYC_STATUSES,
    label: 'Approved',
    downloadName: 'List of Users with Verified KYC',
    labelTitle: 'Approved KYC Users',
  },
  {
    key: 'ACCREDITED',
    filterProperty: 'isAccredited',
    filterPropertyValue: 'ACCREDITED',
    label: 'Accredited',
    downloadName: 'List of Accredited Users',
    labelTitle: 'Accredited Users',
  },
  {
    key: 'FAILED',
    filterProperty: 'kycStatus',
    filterPropertyValue: 'REJECTED',
    label: 'Failed',
    downloadName: 'List of Users with failed KYC Validation',
    labelTitle: 'Failed Verification Users',
  },
  {
    key: 'DISABLED',
    filterProperty: 'deleted',
    filterPropertyValue: true,
    label: 'Disabled',
    downloadName: 'List of Disabled Users',
    labelTitle: 'Disabled Users',
  },
];

export const notificationChannels = [
  'PUSH',
  'SMS',
  'IN_APP',
  'EMAIL',
  'WHATSAPP',
];

export const CAMPAIGN_TYPES = [
  {
    key: 'DRAFT',
    label: 'Draft Listings',
    route: 'draft',
  },
  {
    key: 'ACTIVE',
    label: 'Active Listings',
    route: 'active',
  },
  {
    key: 'ENDED',
    label: 'Ended Listings',
    route: 'ended',
  },
];

export const ORDER_TRADE_STATUS = [
  {
    key: 'REFUND_PENDING',
    value: 'Refund Pending',
  },
  {
    key: 'REFUND_SUCCESSFUL',
    value: 'Refund Successful',
  },
  {
    key: 'CREATED',
    value: 'Created',
  },
  {
    key: 'SETTLED',
    value: 'Settled',
  },
  {
    key: 'FAILED',
    value: 'Failed',
  },
  {
    key: 'FUNDED',
    value: 'Funded',
  },
  {
    key: 'VOIDED',
    value: 'VOIDED',
  },
  {
    key: 'CANCELED',
    value: 'Canceled',
  },
];
export const FADE_RIGHT_ANIMATION_VARIANTS = {
  hidden: {opacity: 0, y: -10},
  show: {opacity: 1, y: 0, transition: {type: 'spring'}},
};

export const supportedLanguages = [
  {value: 'EN', label: 'English'},
  {value: 'FR', label: 'French'},
];

export const QUILL_MODULES = {
  toolbar: [
    [{header: '1'}, {header: '2'}, {header: [3, 4, 5, 6]}, {font: []}],
    [{size: []}],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{list: 'ordered'}, {list: 'bullet'}],
    ['link'],
  ],
};

export const QUILL_FORMATS = [
  'header',
  'size',
  'font',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'link',
  'code-block',
];

export const NavTabsEnum = {
  Overview: 'OVERVIEW',
  Announcements: 'ANNOUNCEMENTS',
  Users: 'USERS',
  UsersNPS: 'USERS_NPS',
  Assets: 'ASSETS',
  UsersTab: 'USERS_TAB',
  keyStatistics: 'KEY_STATISTICS',
  Stocks: 'STOCKS',
  Startups: 'STARTUPS',
  StockListings: 'STOCK_LISTINGS',
  StockPriceAlerts: 'STOCK_PRICE_ALERTS',
  BuySellOrder: 'BUY_SELL_ORDER',
  Listings: 'LISTINGS',
  KycDashboard: 'KYC_DASHBOARD',
  KybDashboard: 'KYB_DASHBOARD',
  Topics: 'TOPICS',
  Support: 'SUPPORT',
  Affiliates: 'AFFILIATES',
  Simulator: 'SIMULATOR',
  Financials: 'FINANCIALS',
  FAQ: 'FAQ',
  SAVINGS: 'SAVINGS',
  AgentHub: 'AGENT_SUCCESS_HUB',
  AgentPerformance: 'AGENT_PERFORMANCE',
  Report: 'REPORT',
  Management: 'MANAGEMENT',
  Updates: 'INVESTOR_UPDATES',
  WaitList: 'WAITLIST',
  News: 'NEWS',
  Groups: 'GROUPS',
  AcademyCodes: 'ACADEMY_COUPON',
  Plans: 'PLANS',
  assetClasses: 'ASSET_CLASSES',
  DocCategory: 'DOC_CATEGORY',
  ListingReports: 'LISTING_REPORTS',
  Dividends: 'DIVIDEND',
  NotificationSchedule: 'NOTIFICATION_SCHEDULE',
  NotificationClass: 'NOTIFICATION_CLASS',
  NotificationOrchestrator: 'NOTIFICATION_ORCHESTRATOR',
  Wallet: 'WALLET_SETTINGS',
  Swap: 'SWAP_SETTINGS',
  AccessCode: 'ACCESS_CODE',
  RewardScheme: 'REWARD_SCHEME',
  RewardBanks: 'REWARD_BANKS',
  ReferralProgram: 'REFERRAL_PROGRAM',
  UsersReferrals: 'USER_REFERRALS',
  DepositSettings: 'DEPOSIT_SETTINGS',
  PlatformSettings: 'PLATFORM_SETTINGS',
  Leaderboard: 'LEADERBOARD',
  SalesAgentCallList: 'SALES_AGENT_CALL_LIST',
  RecurringInvestments: 'RECURRING_INVESTMENTS',
  DabaProReports: 'DABA_PRO_REPORTS',
  BankWireDetails: 'BANK_WIRE_DETAILS',
  CryptoDetails: 'CRYPTO_DETAILS',
  Subscription: 'SUBSCRIPTION',
  Collections: 'COLLECTIONS',
  InstrumentRecommendations: 'INSTRUMENT_RECOMMENDATIONS',
  InstrumentThemes: 'INSTRUMENT_THEMES',
  SalesAgentProspect: 'SALES_AGENT_PROSPECT',
  QuickActions: 'QUICK_ACTIONS',
  AiHelpDesk: 'AI_HELPDESK',
  Analytics: 'ANALYTICS',
  CustomerSupport: 'CUSTOMER_SUPPORT',
  AiAutomationTab: 'AI_AUTOMATION_TAB',
};

import * as G from 'components/nav/glyphs';

const navIcons = {
  // One glyph per destination — see components/nav/glyphs.js for why the old
  // map could not stay (BuildingIcon alone stood for six unrelated tabs).
  Overview: <G.HomeGlyph />,
  Analytics: <G.AnalyticsGlyph />,
  CustomerSupport: <G.SupportGlyph />,
  AiHelpDesk: <G.AutomationGlyph />,
  Users: <G.UsersGlyph />,
  Assets: <G.AssetsGlyph />,
  Stocks: <G.TradingGlyph />,
  Financials: <G.FinancialsGlyph />,
  Dividends: <G.DividendsGlyph />,
  Wallet: <G.WalletGlyph />,
  Notification: <G.NotificationGlyph />,
  Management: <G.ManagementGlyph />,
  Faq: <G.FaqGlyph />,
  Groups: <G.CirclesGlyph />,
  Support: <G.AgentHubGlyph />,
  Announcement: <G.AnnouncementGlyph />,
  Marketing: <G.MarketingGlyph />,
  Listings: <G.ListingsGlyph />,
  Settings: <G.SettingsGlyph />,
  subscription: <G.SubscriptionGlyph />,
  Savings: <G.SavingsGlyph />,
  Topics: <G.TopicsGlyph />,
  Report: <G.ReportGlyph />,
  Plans: <G.PlansGlyph />,

  // Users
  UserList: <G.UserListGlyph />,
  KycDashboard: <G.KycGlyph />,
  KybDashboard: <G.KybGlyph />,
  WaitList: <G.WaitListGlyph />,
  UsersNPS: <G.FeedbackGlyph />,
  Leaderboard: <G.LeaderboardGlyph />,
  QuickActions: <G.QuickActionsGlyph />,

  // Multi Asset
  Instruments: <G.InstrumentsGlyph />,
  Exchanges: <G.ExchangeGlyph />,
  AssetClasses: <G.AssetClassGlyph />,
  Startups: <G.CompaniesGlyph />,
  keyStatistics: <G.KeyStatsGlyph />,
  collections: <G.CollectionsGlyph />,
  instrumentThemes: <G.ThemesGlyph />,

  // Trading View
  MarketOverview: <G.MarketOverviewGlyph />,
  ExchangeConfig: <G.ExchangeConfigGlyph />,
  Orders: <G.OrdersGlyph />,
  StockAlerts: <G.AlertsGlyph />,
  buyAndSell: <G.BuySellGlyph />,
  DocCategory: <G.DocCenterGlyph />,
  RecurringInvestments: <G.RecurringGlyph />,

  // Wallet
  Balances: <G.BalancesGlyph />,
  Swap: <G.SwapGlyph />,

  // Notification
  NotificationSchedule: <G.ScheduleGlyph />,
  NotificationClass: <G.ClassGlyph />,
  NotificationOrchestrator: <G.OrchestratorGlyph />,

  // Management
  Updates: <G.UpdatesGlyph />,
  News: <G.NewsGlyph />,
  AcademyCodes: <G.AcademyGlyph />,
  DabaProReports: <G.ProReportGlyph />,
  Recommendations: <G.RecommendationGlyph />,
  simulator: <G.SimulatorGlyph />,

  // Sales Agent Hub
  AgentHub: <G.InteractionsGlyph />,
  AgentPerformance: <G.PerformanceGlyph />,
  SalesAgentCallList: <G.CallListGlyph />,
  SalesAgentProspect: <G.ProspectGlyph />,

  // Marketing / Listings
  Affiliates: <G.AffiliatesGlyph />,
  ListingOverview: <G.ListingOverviewGlyph />,
  listingReports: <G.ListingReportGlyph />,

  // Settings
  accessCode: <G.AccessCodeGlyph />,
  depositSettings: <G.DepositGlyph />,
  rewardScheme: <G.RewardSchemeGlyph />,
  rewardBanks: <G.RewardBankGlyph />,
  platformSettings: <G.PlatformGlyph />,
  bankWireDetails: <G.BankWireGlyph />,
  cryptoDetails: <G.CryptoGlyph />,

  // Savings
  SavingsOverview: <G.SavingsOverviewGlyph />,
  SavingsCompanies: <G.OrganizationsGlyph />,
  savingsProducts: <G.ProductsGlyph />,
};

export const ADMIN_TABS = {
  Overview: {
    name: 'Overview',
    tab: NavTabsEnum.Overview,
    icon: navIcons.Overview,
    to: '',
  },
  Analytics: {
    name: 'Analytics',
    tab: NavTabsEnum.Analytics,
    icon: navIcons.Analytics,
    to: 'analytics',
  },
  AiHelpDesk: {
    name: 'Customer Support',
    // A group, not a destination: its own key (like Wallet's) so login's
    // first-tab lookup falls through to Tickets instead of matching a group
    // with nowhere to go.
    tab: 'CUSTOMER_SUPPORT_GROUP',
    icon: navIcons.CustomerSupport,
    inner: {
      tickets: {
        name: 'Tickets',
        tab: NavTabsEnum.CustomerSupport,
        icon: navIcons.CustomerSupport,
        to: 'ai-helpdesk',
      },
      // Helpdesk analytics. SUPERADMIN only — the nav hides it from every
      // other role, and the page itself refuses them too.
      performance: {
        name: 'Performance',
        tab: NavTabsEnum.CustomerSupport,
        icon: navIcons.Analytics,
        to: 'support-performance',
        superAdminOnly: true,
        noBadge: true,
      },
      // An agent's own numbers. The page asks the server for the signed-in
      // agent only; there is no team roster or agent picker on it.
      myPerformance: {
        name: 'My performance',
        tab: NavTabsEnum.CustomerSupport,
        icon: navIcons.Analytics,
        to: 'support-performance/me',
        onlyRoles: ['CUSTOMER_SUPPORT'],
        noBadge: true,
      },
    },
  },
  aiAutomation: {
    name: 'AI & Automation',
    tab: NavTabsEnum.AiAutomationTab,
    icon: navIcons.AiHelpDesk,
    to: 'ai-automation',
  },
  UsersTab: {
    name: 'Users',
    icon: navIcons.Users,
    tab: NavTabsEnum.UsersTab,
    inner: {
      Users: {
        name: 'List of Users',
        tab: NavTabsEnum.Users,
        icon: navIcons.UserList,
        to: 'users',
      },
      KycDashboard: {
        name: 'KYC Dashboard',
        tab: NavTabsEnum.KycDashboard,
        icon: navIcons.KycDashboard,
        to: 'kyc-dashboard',
      },
      KybDashboard: {
        name: 'Business Verification',
        tab: NavTabsEnum.KybDashboard,
        icon: navIcons.KybDashboard,
        to: 'kyb-dashboard',
      },
      WaitList: {
        name: 'Wait List',
        tab: NavTabsEnum.WaitList,
        icon: navIcons.WaitList,
        to: 'wait-list',
      },
      UsersNPS: {
        name: 'Users Feedback',
        tab: NavTabsEnum.UsersNPS,
        icon: navIcons.UsersNPS,
        to: 'customers/nps',
      },
      Leaderboard: {
        name: 'Leaderboard',
        tab: NavTabsEnum.Leaderboard,
        icon: navIcons.Leaderboard,
        to: 'leaderboard',
      },
      QuickActions: {
        name: 'Quick Actions',
        tab: NavTabsEnum.QuickActions,
        icon: navIcons.QuickActions,
        to: 'quick-actions',
      },
    },
  },
  AssetManagement: {
    name: 'Multi Asset',
    tab: 'ASSET_MANAGEMENT',
    icon: navIcons.Assets,
    inner: {
      // The day-to-day workspace: every instrument, seen through the shared
      // exchange lens. Reuses the ASSET_CLASSES tab key deliberately — a new
      // key would be invisible to every non-superadmin until the backend
      // grants it, and this ships without a role change.
      Instruments: {
        name: 'Instruments',
        tab: NavTabsEnum.assetClasses,
        icon: navIcons.Instruments,
        to: 'instruments',
      },
      // Venue definition. Moved out of Trading View → Orders, where it sat as
      // a tab inside an order-management screen.
      Exchanges: {
        name: 'Exchanges',
        tab: NavTabsEnum.assetClasses,
        icon: navIcons.Exchanges,
        to: 'exchanges',
      },
      AssetClasses: {
        name: 'Asset Classes',
        tab: NavTabsEnum.assetClasses,
        icon: navIcons.AssetClasses,
        to: 'asset-management/classes',
      },
      Startups: {
        name: 'Companies',
        tab: NavTabsEnum.Startups,
        icon: navIcons.Startups,
        to: 'opportunities/startups',
      },
      keyStatistics: {
        name: 'Key Statistics',
        tab: NavTabsEnum.keyStatistics,
        icon: navIcons.keyStatistics,
        to: 'key-statistics',
      },
      collections: {
        name: 'Collections',
        tab: NavTabsEnum.Collections,
        icon: navIcons.collections,
        to: 'collections',
      },
      instrumentThemes: {
        name: 'Themes',
        tab: NavTabsEnum.InstrumentThemes,
        icon: navIcons.instrumentThemes,
        to: 'instrument-themes',
      },
    },
  },
  Stocks: {
    name: 'Trading View',
    icon: navIcons.Stocks,
    tab: NavTabsEnum.Stocks,
    inner: {
      // Read-only venue state. Same reuse of STOCK_LISTINGS as above.
      MarketOverview: {
        name: 'Market Overview',
        tab: NavTabsEnum.StockListings,
        icon: navIcons.MarketOverview,
        to: 'stocks/market-overview',
      },
      // Venue trading behaviour — operational, so it sits beside the orders
      // it affects rather than with the branding fields in Multi Asset.
      TradingConfig: {
        name: 'Exchange Config',
        tab: NavTabsEnum.StockListings,
        icon: navIcons.ExchangeConfig,
        to: 'stocks/exchange-config',
      },
      Listings: {
        name: 'Orders',
        tab: NavTabsEnum.StockListings,
        icon: navIcons.Orders,
        to: 'stocks/listings',
      },
      priceAlerts: {
        name: 'Alerts',
        tab: NavTabsEnum.StockPriceAlerts,
        icon: navIcons.StockAlerts,
        to: 'stocks/alerts',
      },
      buyAndSell: {
        name: 'Buy/Sell V1',
        tab: NavTabsEnum.BuySellOrder,
        icon: navIcons.buyAndSell,
        to: 'stocks/user-trade',
      },
      DocCategory: {
        name: 'Document Center',
        tab: NavTabsEnum.DocCategory,
        icon: navIcons.DocCategory,
        to: 'startup/document-category',
      },
      recurringInvestments: {
        name: 'Recurring Investments',
        tab: NavTabsEnum.RecurringInvestments,
        icon: navIcons.RecurringInvestments,
        to: 'recurring-investments',
      },
    },
  },
  Financials: {
    name: 'Financials',
    to: 'financial',
    icon: navIcons.Financials,
    tab: NavTabsEnum.Financials,
  },
  Dividends: {
    name: 'Dividends',
    icon: navIcons.Dividends,
    tab: NavTabsEnum.Dividends,
    to: 'dividends/payouts',
  },
  Wallet: {
    name: 'Wallet',
    icon: navIcons.Wallet,
    tab: 'WALLET',
    inner: {
      walletSettings: {
        name: 'Wallet',
        tab: NavTabsEnum.Wallet,
        icon: navIcons.Balances,
        to: 'wallet',
      },
      swapSettings: {
        name: 'Swap Settings',
        tab: NavTabsEnum.Swap,
        icon: navIcons.Swap,
        to: 'fx-exchange-rate',
      },
      // Straight to every swap; currency and date filters are picked there.
      swapTransactions: {
        name: 'Swap Transactions',
        tab: NavTabsEnum.Swap,
        icon: navIcons.Swap,
        to: 'wallet/swap-transactions/All',
      },
      transferLookup: {
        name: 'Transfer Lookup',
        tab: NavTabsEnum.Wallet,
        icon: navIcons.Balances,
        to: 'wallet/transfer-lookup',
      },
    },
  },
  Notification: {
    name: 'Notification',
    icon: navIcons.Notification,
    tab: NavTabsEnum.Notification,
    inner: {
      notificationSchedule: {
        name: 'Schedule',
        icon: navIcons.NotificationSchedule,
        to: 'notifications',
        tab: NavTabsEnum.NotificationSchedule,
      },
      notificationClass: {
        name: 'Class',
        icon: navIcons.NotificationClass,
        to: 'notifications/class',
        tab: NavTabsEnum.NotificationClass,
      },
      notificationOrchestrator: {
        name: 'Workflow Orchestrator',
        icon: navIcons.NotificationOrchestrator,
        to: 'notifications/workflow-orchestrator',
        tab: NavTabsEnum.NotificationOrchestrator,
      },
    },
  },
  Management: {
    name: 'Management',
    icon: navIcons.Management,
    tab: NavTabsEnum.Management,
    inner: {
      Updates: {
        name: 'Updates',
        tab: NavTabsEnum.Updates,
        icon: navIcons.Updates,
        to: 'explore',
      },
      News: {
        name: 'News',
        tab: NavTabsEnum.News,
        icon: navIcons.News,
        to: 'news',
      },
      AcademyCodes: {
        name: 'Academy Codes',
        tab: NavTabsEnum.AcademyCodes,
        icon: navIcons.AcademyCodes,
        to: 'academy-codes',
      },
      DabaPro: {
        name: 'Daba Pro Reports',
        tab: NavTabsEnum.DabaProReports,
        icon: navIcons.DabaProReports,
        to: 'daba-pro',
      },
      StockRecommendations: {
        name: 'Recommendations',
        tab: NavTabsEnum.InstrumentRecommendations,
        icon: navIcons.Recommendations,
        to: 'stock-recommendations',
      },
      simulator: {
        name: 'Simulator',
        tab: NavTabsEnum.Simulator,
        icon: navIcons.simulator,
        to: 'simulator',
      },
    },
  },
  Faq: {
    name: 'FAQ & Feedbacks',
    icon: navIcons.Faq,
    tab: NavTabsEnum.FAQ,
    to: 'support/Faq',
  },
  Groups: {
    name: 'Circles',
    icon: navIcons.Groups,
    tab: NavTabsEnum.Groups,
    to: 'groups',
  },
  Support: {
    name: 'Sales Agent Hub',
    tab: NavTabsEnum.Support,
    icon: navIcons.Support,
    inner: {
      AgentHub: {
        name: 'Interactions',
        tab: NavTabsEnum.AgentHub,
        icon: navIcons.AgentHub,
        to: 'customer-success-hub',
      },
      AgentPerformance: {
        name: 'Performance',
        tab: NavTabsEnum.AgentPerformance,
        icon: navIcons.AgentPerformance,
        to: 'customer-performance',
      },
      SalesAgentCallList: {
        name: 'Call List',
        tab: NavTabsEnum.SalesAgentCallList,
        icon: navIcons.SalesAgentCallList,
        to: 'sales-agent-call-list',
      },
      SalesAgentProspect: {
        name: 'Prospect',
        tab: NavTabsEnum.SalesAgentProspect,
        icon: navIcons.SalesAgentProspect,
        to: 'sales-agent-prospect',
      },
    },
  },

  Announcement: {
    name: 'Announcements',
    tab: NavTabsEnum.Announcements,
    icon: navIcons.Announcement,
    to: 'announcements',
  },
  Marketing: {
    name: 'Marketing',
    icon: navIcons.Marketing,
    tab: NavTabsEnum.Dividends,
    inner: {
      Affiliates: {
        name: 'Affiliates',
        tab: NavTabsEnum.Affiliates,
        icon: navIcons.Affiliates,
        to: 'affiliates',
      },
    },
  },

  Listings: {
    name: 'Listings',
    tab: 'LISTINGS_OVERVIEW',
    icon: navIcons.Listings,
    inner: {
      ListingsOverview: {
        name: 'Listing Overview',
        tab: NavTabsEnum.Listings,
        icon: navIcons.ListingOverview,
        to: 'campaigns',
      },
      ListingReports: {
        name: 'Listing Reports',
        tab: NavTabsEnum.ListingReports,
        icon: navIcons.listingReports,
        to: 'listings/reports',
      },
    },
  },
  Settings: {
    name: 'Settings',
    icon: navIcons.Settings,
    tab: NavTabsEnum.Settings,
    inner: {
      accessCode: {
        name: 'Access Code',
        icon: navIcons.accessCode,
        tab: NavTabsEnum.AccessCode,
        to: 'access-code',
      },
      // PAGE: ON HOLD FOR NOW
      // referralProgram: {
      //   name: 'Referral Program',
      //   icon: navIcons.referralProgram,
      //   tab: NavTabsEnum.ReferralProgram,
      //   to: 'referral-program',
      // },
      depositSettings: {
        name: 'Deposit Settings',
        icon: navIcons.depositSettings,
        tab: NavTabsEnum.DepositSettings,
        to: 'deposit-settings',
      },
      rewardScheme: {
        name: 'Reward Scheme',
        icon: navIcons.rewardScheme,
        tab: NavTabsEnum.RewardScheme,
        to: 'reward-schemes',
      },
      rewardBanks: {
        name: 'Reward Banks',
        icon: navIcons.rewardBanks,
        tab: NavTabsEnum.RewardBanks,
        to: 'reward-banks',
      },
      platformSettings: {
        name: 'Platform Settings',
        icon: navIcons.platformSettings,
        tab: NavTabsEnum.PlatformSettings,
        to: 'app-version-setting',
      },
      bankWireDetails: {
        name: 'Bank Wire Details',
        icon: navIcons.bankWireDetails,
        tab: NavTabsEnum.BankWireDetails,
        to: 'bank-details',
      },
      cryptoDetails: {
        name: 'Crypto Details',
        icon: navIcons.cryptoDetails,
        tab: NavTabsEnum.CryptoDetails,
        to: 'crypto-setting',
      },
    },
  },
  Subscription: {
    name: 'Subscription',
    icon: navIcons.subscription,
    tab: NavTabsEnum.Subscription,
    to: 'plans/subscription-setting/plans',
  },
  Savings: {
    name: 'Savings',
    tab: NavTabsEnum.SAVINGS,
    icon: navIcons.Savings,
    hideOnProd: !isDev,
    inner: {
      SavingsOverview: {
        name: 'Overview',
        tab: NavTabsEnum.SAVINGS,
        icon: navIcons.SavingsOverview,
        to: 'savings',
      },
      SavingsCompanies: {
        name: 'Organizations',
        to: 'savings/companies',
        tab: NavTabsEnum.SAVINGS,
        icon: navIcons.SavingsCompanies,
      },
      savingsProducts: {
        name: 'Products',
        to: 'savings/products',
        icon: navIcons.savingsProducts,
      },
    },
  },
  Topics: {
    name: 'Topics',
    icon: navIcons.Topics,
    tab: NavTabsEnum.Topics,
    to: 'interest',
  },

  Report: {
    name: 'Report',
    tab: NavTabsEnum.Report,
    icon: navIcons.Report,
    to: 'report',
  },

  Plans: {
    name: 'Plans',
    tab: NavTabsEnum.Plans,
    icon: navIcons.Plans,
    to: 'plans',
  },
};

export const ADMIN_TAB_FIELDS = {
  OVERVIEW: 'Overview',
  ANALYTICS: 'Analytics',
  CUSTOMER_SUPPORT: 'Customer Support',
  AI_AUTOMATION_TAB: 'AI & Automation',
  ANNOUNCEMENTS: 'Announcements',
  USERS: 'Users',
  USERS_NPS: 'User NPS',
  STARTUPS: 'Startups',
  KEY_STATISTICS: 'Key Statistics',
  STOCK_LISTINGS: 'Trading View',
  STOCK_PRICE_ALERTS: 'Alerts',
  BUY_SELL_ORDER: 'Buy/Sell Order',
  LISTINGS: 'Listings',
  TOPICS: 'Topics',
  FAQ: 'FAQ',
  REPORT: 'Report',
  FINANCIALS: 'Financials',
  SALES_AGENT_PROSPECT: 'Sales Agent Prospect',
  NEWS: 'News',
  GROUPS: 'Groups',
  ACADEMY_COUPON: 'Academy Promo Codes',
  PLANS: 'Plans',
  DOC_CATEGORY: 'Document Center',
  NOTIFICATION_SCHEDULE: 'Notification Schedule',
  WALLET_SETTINGS: 'Wallet',
  DOC_CATEGORY: 'Document Center',
  AGENT_PERFORMANCE: 'Agent Performance',
  INSTRUMENT_RECOMMENDATIONS: 'Recommendations',
  ASSET_CLASSES: 'Asset Classes',
  ACCESS_CODE: 'Access Code',
  AGENT_SUCCESS_HUB: 'Agent Hub',
  REFERRAL_PROGRAM: 'Referral Program',
  RECURRING_INVESTMENTS: 'Recurring Investments',
  KYC_DASHBOARD: 'KYC Dashboard',
  KYB_DASHBOARD: 'Business Verification',
  USERS_REFERRALS: 'User Referral',
  DEPOSIT_SETTINGS: 'Deposit Settings',
  PLATFORM_SETTINGS: 'Platform Settings',
  LEADERBOARD: 'Leaderboard',
  SIMULATOR: 'Simulator',
  BANK_WIRE_DETAILS: 'Bank Wire Details',
  CRYPTO_DETAILS: 'Crypto Details',
  SUBSCRIPTION: 'Subscription',
  DIVIDEND: 'Dividend',
  NOTIFICATION_CLASS: 'Notification Class',
  NOTIFICATION_ORCHESTRATOR: 'Workflow Orchestrator',
  QUICK_ACTIONS: 'Quick Actions',
  DABA_PRO_REPORTS: 'Daba Pro Reports',
  INVESTOR_UPDATES: 'Investor Updates',
  REWARD_SCHEME: 'Reward Scheme',
  SALES_AGENT_CALL_LIST: 'Sales Agent Call List',
  REWARD_BANKS: 'Reward Banks',
  SWAP_SETTINGS: 'Swap Settings',
};

export const extractNamedTabFields = tabs => {
  const namedTabs = [];
  Object.keys(tabs).forEach(key => {
    const tab = tabs[key];
    if (tab.inner) {
      // If tab has inner objects, recursively extract from inner tabs
      const innerNamedTabs = extractNamedTabFields(tab.inner);
      namedTabs.push(...innerNamedTabs);
    } else if (tab.name && (tab.to || tab.to === '')) {
      // If tab has both name and to fields, add its name to the array
      namedTabs.push(tab.name);
    }
  });
  return namedTabs;
};

export const monthsOfTheYear = [
  {key: 'JAN', value: 'January'},
  {key: 'FEB', value: 'February'},
  {key: 'MAR', value: 'March'},
  {key: 'APR', value: 'April'},
  {key: 'MAY', value: 'May'},
  {key: 'JUN', value: 'June'},
  {key: 'JUL', value: 'July'},
  {key: 'AUG', value: 'August'},
  {key: 'SEP', value: 'September'},
  {key: 'OCT', value: 'October'},
  {key: 'NOV', value: 'November'},
  {key: 'DEC', value: 'December'},
];

export const usersSegments = [
  {key: 'PORTFOLIO_CONCENTRATION_RISK', value: 'Portfolio Concentration Risk'},
  {key: 'HIGH_CASH_POSITION', value: 'High Cash Position'},
  {key: 'DEPOSIT_TO_INVESTMENT_GAP', value: 'Deposit to Investment Gap'},
  {key: 'ASSET_CLASS_RESEARCH_PATTERN', value: 'Asset Class Research Pattern'},
  {
    key: 'RECENT_LIQUIDATION_NO_REINVESTMENT',
    value: 'Recent Liquidation No Reinvestment',
  },
  {key: 'RESEARCH_HEAVY_LOW_ACTION', value: 'Research Heavy Low Action'},
  {
    key: 'HIGH_AUM_HIGH_ACTIVITY_NO_PRO',
    value: 'High AUM High Activity No Pro',
  },
  {
    key: 'PERFORMANCE_TRACKING_OBSESSION',
    value: 'Performance Tracking Obsession',
  },
  {
    key: 'VIEWED_DABA_PRO_PAGE_NO_SUBSCRIPTION',
    value: 'Viewed Daba Pro Page No Subscription',
  },
  {key: 'FUNDED_BUT_INACTIVE', value: 'Funded But Inactive'},
  {key: 'SMALL_FREQUENT_DEPOSITS', value: 'Small Frequent Deposits'},
  {key: 'ENGAGEMENT_EXECUTION_GAP', value: 'Engagement Execution Gap'},
  {key: 'NEW_USER_NO_INVESTMENT', value: 'New User No Investment'},
  {
    key: 'INVESTMENT_FREQUENCY_MISMATCH',
    value: 'Investment Frequency Mismatch',
  },
  {
    key: 'WATCHLIST_ACROSS_ASSET_CLASSES',
    value: 'Watchlist Across Asset Classes',
  },
  {
    key: 'FIRST_TIME_INVESTOR_STOCKS_ONLY',
    value: 'First Time Investor Stocks Only',
  },
  {
    key: 'MARKET_VOLATILITY_SPIKE_ENGAGEMENT',
    value: 'Market Volatility Spike Engagement',
  },
  {key: 'PREMIUM_CONTENT_CONSUMPTION', value: 'Premium Content Consumption'},
  {key: 'TRIAL_ENDED_NO_CONVERSION', value: 'Trial Ended No Conversion'},
  {key: 'PORTFOLIO_TOOL_USAGE', value: 'Portfolio Tool Usage'},
  {key: 'SCREENER_POWER_USER', value: 'Screener Power User'},
  {
    key: 'BASIC_INVESTMENT_QUESTIONS_TO_SUPPORT',
    value: 'Basic Investment Questions To Support',
  },
  {
    key: 'CONSERVATIVE_HOLDINGS_GROWTH_CONTENT',
    value: 'Conservative Holdings Growth Content',
  },
  {
    key: 'TUTORIAL_COMPLETION_NO_INVESTMENT',
    value: 'Tutorial Completion No Investment',
  },
  {key: 'RISK_PROFILE_MISMATCH', value: 'Risk Profile Mismatch'},
  {
    key: 'RAPID_AUM_GROWTH_BASIC_PORTFOLIO',
    value: 'Rapid AUM Growth Basic Portfolio',
  },
];

export const orderFieldMappings = {
  'BROKER IDS': {
    'fixed': false,
    'value': '',
    'key': 'brokerIds',
  },
  'PREVIOUS BROKER IDS': {
    'fixed': false,
    'value': '',
    'key': 'previousBrokerIds',
  },
  'PAST BROKER IDS': {
    'fixed': false,
    'value': '',
    'key': 'pastBrokerIds',
  },
  'ALLOCATION': {
    'fixed': false,
    'value': '',
    'key': 'allocation',
  },
  'PROPORTION': {
    'fixed': false,
    'value': '',
    'key': 'proportion',
  },
  'LAST FILL DATE': {
    'fixed': false,
    'value': '',
    'key': 'lastFillDate',
  },
  'LAST FILL PRICE': {
    'fixed': false,
    'value': '',
    'key': 'lastFillPrice',
  },
  'AVG FILL PRICE': {
    'fixed': false,
    'value': '',
    'key': 'avgFillPrice',
  },
  'TOTAL QUANTITY': {
    'fixed': false,
    'value': '',
    'key': 'quantity',
  },
  'FILL QUANTITY': {
    'fixed': false,
    'value': '',
    'key': 'fillQuantity',
  },
  'CREATED DATE': {
    'fixed': false,
    'value': '',
    'key': 'createdAt',
  },
  'STATUS': {
    'fixed': false,
    'value': '',
    'key': 'status',
  },
  'ACCOUNT NUMBER': {
    'fixed': false,
    'value': '',
    'key': 'broker.accountNumber',
  },
  'TICKER/ISIN': {
    'fixed': false,
    'value': '',
    'key': 'symbol',
  },
  'TYPE': {
    'fixed': false,
    'value': '',
    'key': 'type',
  },
  'CURRENCY': {
    'fixed': false,
    'value': '',
    'key': 'assetCurrency',
  },
  'QUANTITY': {
    'fixed': false,
    'value': '',
    'key': 'fillQuantityLeft',
  },
  'PRICE': {
    'fixed': false,
    'value': '',
    'key': 'limitPrice',
  },
  'TOTAL AMOUNT ESTIMATE': {
    'fixed': false,
    'value': '',
    'key': 'price',
  },
  'DIRECTION': {
    'fixed': false,
    'value': '',
    'key': 'direction',
  },
  'EXPIRATION DATE': {
    'fixed': false,
    'value': '',
    'key': 'expiresAt',
  },
  'DABA ORDER ID': {
    'fixed': false,
    'value': '',
    'key': '_id',
  },
  'DABA BUNDLE ID': {
    'fixed': false,
    'value': '',
    'key': 'bundleId',
  },
  'ORIGINATION CHANNEL': {
    'fixed': true,
    'value': 'APP',
    'key': '',
  },
  'BROKER ORDER ID': {
    'fixed': true,
    'value': '',
    'key': '',
  },
  'BROKER ORDER QUANTITY': {
    'fixed': true,
    'value': '',
    'key': '',
  },
  'BROKER ORDER PRICE': {
    'fixed': true,
    'value': '',
    'key': '',
  },
  'BROKER': {
    'fixed': true,
    'value': '',
    'key': '',
  },
  'NOTES': {
    'fixed': true,
    'value': '',
    'key': '',
  },
};

// OfferingPartyRole — mirrors the gateway enum exactly.
export const offeringPartyRoles = [
  {label: 'Underwriter', value: 'UNDERWRITER'},
  {label: 'Lead arranger', value: 'LEAD_ARRANGER'},
  {label: 'Registrar', value: 'REGISTRAR'},
  {label: 'Issuing house', value: 'ISSUING_HOUSE'},
  {label: 'Fund manager', value: 'FUND_MANAGER'},
];

// ReturnPeriod — trailing windows only. The YEAR member is deliberately
// excluded: it marks a calendar-year record, which is edited as a year number
// rather than picked from this list. Trailing and calendar-year entries are
// never mixed in one list.
export const trailingReturnPeriods = [
  {label: 'Today', value: 'TODAY'},
  {label: '1 Month', value: 'ONE_MONTH'},
  {label: '3 Months', value: 'THREE_MONTH'},
  {label: '6 Months', value: 'SIX_MONTH'},
  {label: '1 Year', value: 'ONE_YEAR'},
  {label: '3 Years', value: 'THREE_YEAR'},
  {label: '5 Years', value: 'FIVE_YEAR'},
  {label: 'Year to date', value: 'YTD'},
  {label: 'Since inception', value: 'SINCE_INCEPTION'},
];

// ReturnMetric — mirrors the gateway enum exactly.
export const returnMetrics = [
  {label: 'Return', value: 'RETURN'},
  {label: 'Yield', value: 'YIELD'},
  {label: 'CAGR', value: 'CAGR'},
];

// FundStructure — mirrors the gateway enum exactly.
// Currently unused: `fundStructure` is readable on the MutualFund type but has
// no matching field on MutualFundInput, so it cannot be written. Wire the form
// field back up once the backend adds it.
export const fundStructures = [
  {label: 'Open ended', value: 'OPEN_ENDED'},
  {label: 'Closed ended', value: 'CLOSED_ENDED'},
];

// IpoStatus — mirrors the gateway enum exactly. Stored, and never advances on
// its own, so admin treats it as a manual override (§2).
export const ipoStatuses = [
  {label: 'Draft', value: 'DRAFT'},
  {label: 'Coming soon', value: 'COMING_SOON'},
  {label: 'Upcoming', value: 'UPCOMING'},
  {label: 'Open', value: 'OPEN'},
  {label: 'Closed', value: 'CLOSED'},
  {label: 'Allocating', value: 'ALLOCATING'},
  {label: 'Listed', value: 'LISTED'},
  {label: 'Cancelled', value: 'CANCELLED'},
];
